'use client'

import { Badge } from '@brew/ui/badge'
import { useOrganization, useUser } from '@clerk/nextjs'
import { LayoutGrid, Loader2 } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useCallback, useState } from 'react'
import { buildFocusedEmailCanvasUrl } from '@/lib/canvas/email-canvas-url'
import { appToast } from '@/lib/shared/toast'

interface AddToCanvasButtonProps {
  emailId: string
  /**
   * Active brand to clone the email into. When provided, the cloned email is
   * scoped to this brand so it appears on the active brand's canvas. When
   * omitted, the clone preserves the source row's brand fields (typically
   * `undefined` for forwarded emails — i.e. the "No Brand" canvas).
   *
   * Plumbed in as a prop from the server so the same component can render
   * in both the signed-in `(app)` shell (where `BrandContextProvider` is
   * available) and the signed-out `(public)/browse/templates` mirror
   * (which does not mount it).
   */
  brandId?: string
}

export function AddToCanvasButton({
  emailId,
  brandId,
}: AddToCanvasButtonProps) {
  const t = useTranslations('templates')
  const router = useRouter()
  const { organization } = useOrganization()
  const { user } = useUser()
  const [isLoading, setIsLoading] = useState(false)

  const orgId = organization?.id
  const userId = user?.id

  const handleAddToCanvas = useCallback(async () => {
    if (!orgId || !brandId) {
      return
    }
    setIsLoading(true)
    try {
      // ONE server action: waits for/adopts the source's in-flight
      // conversion, clones, and converts the clone inline when needed —
      // the email that lands on the canvas is already fully editable (or,
      // on a verified failure, the pixel-exact import, silently). The
      // button's spinner covers the whole journey; the canvas never shows
      // a converting state.
      const response = await fetch('/api/email-import/add-to-canvas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailId, brandId }),
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as {
          error?: string
        } | null
        appToast.error(payload?.error ?? t('addToCanvas.failed'))
        return
      }
      const result = (await response.json()) as { emailId: string }
      // Adding a design never navigates: the user stays on the template and
      // opens the copy from the toast when they want it.
      const canvasHref = buildFocusedEmailCanvasUrl(result.emailId, 'create')
      appToast.success(t('addToCanvas.added'), {
        action: {
          label: t('addToCanvas.open'),
          onClick: () => router.push(canvasHref),
        },
      })
    } catch {
      appToast.error(t('addToCanvas.failed'))
    } finally {
      setIsLoading(false)
    }
  }, [emailId, orgId, brandId, router, t])

  if (!orgId || !brandId || !userId) {
    return null
  }

  return (
    <button
      type="button"
      onClick={() => void handleAddToCanvas()}
      disabled={isLoading}
      title={t('addToCanvas.title')}
      className="cursor-pointer disabled:cursor-default disabled:opacity-60"
    >
      <Badge variant="outline" className="gap-1 text-muted-foreground">
        {isLoading ? (
          <Loader2 className="size-3 animate-spin" />
        ) : (
          <LayoutGrid className="size-3" />
        )}
        {t('addToCanvas.label')}
      </Badge>
    </button>
  )
}
